import { Client } from '../types/entities';

export const CLIENT_OPTIONAL_TEXT_FIELD_MAX_LENGTH = 100;

export function trimOptionalClientText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, CLIENT_OPTIONAL_TEXT_FIELD_MAX_LENGTH);
}

/** Normalise un téléphone pour détection de doublons (chiffres uniquement). */
export function normalizeClientPhoneForDedup(phone: string): string {
  return phone.replace(/\D/g, '');
}

function toDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return undefined;
}

/** Mappe un document Firestore vers le type Client. */
export function mapFirestoreDocToClient(id: string, data: Record<string, unknown>): Client {
  const birthDate = toDate(data.birthDate);
  const lastVisitDate = toDate(data.lastVisitDate);
  const createdAt = toDate(data.createdAt) ?? new Date();
  const updatedAt = toDate(data.updatedAt) ?? new Date();

  const passId =
    typeof data.passId === 'string' && data.passId.trim()
      ? data.passId.trim()
      : undefined;

  return {
    id,
    name: String(data.name ?? ''),
    phone: String(data.phone ?? ''),
    email: data.email ? String(data.email) : undefined,
    businessId: String(data.businessId ?? ''),
    loyaltyBalance: Number(data.loyaltyBalance) || 0,
    ...(birthDate ? { birthDate } : {}),
    ...(lastVisitDate ? { lastVisitDate } : {}),
    ...(passId ? { passId } : {}),
    address: trimOptionalClientText(data.address),
    city: trimOptionalClientText(data.city),
    district: trimOptionalClientText(data.district),
    gender: data.gender as Client['gender'] | undefined,
    emergencyContact: data.emergencyContact as Client['emergencyContact'] | undefined,
    preferences: data.preferences as Client['preferences'] | undefined,
    clientType: trimOptionalClientText(data.clientType),
    createdAt,
    updatedAt,
  };
}

/** Champs texte optionnels à persister à la création client. */
export function applyOptionalClientTextFields(
  target: Record<string, unknown>,
  client: Partial<Pick<Client, 'address' | 'city' | 'district' | 'clientType' | 'phone' | 'email'>>
): void {
  const phone = trimOptionalClientText(client.phone);
  if (phone) target.phone = phone;

  const email = trimOptionalClientText(client.email);
  if (email) target.email = email;

  const address = trimOptionalClientText(client.address);
  if (address) target.address = address;

  const city = trimOptionalClientText(client.city);
  if (city) target.city = city;

  const district = trimOptionalClientText(client.district);
  if (district) target.district = district;

  const clientType = trimOptionalClientText(client.clientType);
  if (clientType) target.clientType = clientType;
}

/** Champs utilisés pour la recherche locale des clients. */
export function getClientSearchableFields(client: Client): (string | undefined)[] {
  return [
    client.name,
    client.phone,
    client.email,
    client.city,
    client.district,
    client.address,
  ];
}
